import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { getClinicInfo, updateClinicInfo } from '../lib/api';
import LegalIdentityCard from '../components/legal/LegalIdentityCard';
import LegalCommercialCard from '../components/legal/LegalCommercialCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Trash2, Save } from 'lucide-react';
import SiteImageField from '../components/SiteImageField';
import { Switch } from '../components/ui/switch';
import { applyThemeFromClinicInfo } from '../lib/themeColors';
import {
  THEME_PRESETS,
  findMatchingThemePreset,
  themePresetToFields,
} from '../lib/themePresets';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BrandingPreview,
  FeaturesPreview,
  HeroPreview,
  LowerSectionsPreview,
  NavPreview,
  ServicesSectionPreview,
  ThemePreview,
} from '../components/sitePublicPreviews';

const FEATURE_ICON_OPTIONS = [
  'shield',
  'heart',
  'clock',
  'calendar',
  'sparkles',
  'star',
  'thumbs-up',
  'users',
  'award',
  'check-circle',
];

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

const DEFAULT_FEATURES_POD = [
  { icon: 'shield', title: 'Profesionalismo', desc: 'Personal certificado y capacitado' },
  { icon: 'heart', title: 'Cuidado Integral', desc: 'Atención personalizada para cada paciente' },
  { icon: 'clock', title: 'Puntualidad', desc: 'Respetamos tu tiempo' },
  { icon: 'calendar', title: 'Flexibilidad', desc: 'Horarios que se adaptan a ti' },
];

const DEFAULT_FEATURES_PIZAS = [
  { icon: 'heart', title: 'Ingredientes frescos', desc: 'Masa y toppings de calidad' },
  { icon: 'clock', title: 'Entrega a tiempo', desc: 'Te avisamos cuando va en camino' },
  { icon: 'sparkles', title: 'Especialidades', desc: 'Recetas con nombre propio' },
  { icon: 'star', title: 'Pide fácil', desc: 'Web o WhatsApp' },
];

const DEFAULT_FEATURES = PIZAS_MODE ? DEFAULT_FEATURES_PIZAS : DEFAULT_FEATURES_POD;

/** Tarjeta de sección con editor a la izquierda y vista previa en vivo a la derecha. */
function SectionCard({ title, hint, children, preview }) {
  return (
    <Card className="scroll-mt-24">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {hint ? <p className="text-sm text-muted-foreground font-normal">{hint}</p> : null}
      </CardHeader>
      <CardContent>
        <div className="grid gap-8 xl:grid-cols-2 xl:items-start">
          <div className="space-y-4">{children}</div>
          <div className="xl:sticky xl:top-4">{preview}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminSitePublic() {
  const { refresh: refreshBranding } = useClinicBranding();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [servicesText, setServicesText] = useState('');
  const [featureRows, setFeatureRows] = useState(DEFAULT_FEATURES);
  const [themePresetId, setThemePresetId] = useState('custom');

  useEffect(() => {
    if (!authLoading && user && !isSuperAdmin(user.role)) {
      toast.error('Solo el super administrador puede editar el sitio público.');
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || !isSuperAdmin(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await getClinicInfo();
        if (cancelled) return;
        setDraft(d);
        applyThemeFromClinicInfo(d);
        setThemePresetId(findMatchingThemePreset(d));
        setServicesText(Array.isArray(d.services_bullets) ? d.services_bullets.join('\n') : '');
        if (Array.isArray(d.home_features) && d.home_features.length > 0) {
          setFeatureRows(d.home_features.map((x) => ({ icon: x.icon, title: x.title, desc: x.desc })));
        } else {
          setFeatureRows(DEFAULT_FEATURES);
        }
      } catch {
        toast.error(PIZAS_MODE ? 'No se pudo cargar la información del sitio.' : 'No se pudo cargar la información de la clínica.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setField = (key, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
      if (key.startsWith('theme_')) applyThemeFromClinicInfo(next);
      return next;
    });
  };

  useEffect(() => {
    if (!draft) return;
    setThemePresetId(findMatchingThemePreset(draft));
  }, [
    draft?.theme_primary_hex,
    draft?.theme_background_hex,
    draft?.theme_foreground_hex,
    draft?.theme_accent_hex,
    draft?.theme_muted_hex,
  ]);

  const applyThemePreset = (presetId) => {
    if (presetId === 'custom') {
      setThemePresetId('custom');
      return;
    }
    const preset = THEME_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...themePresetToFields(preset) };
      applyThemeFromClinicInfo(next);
      return next;
    });
    setThemePresetId(presetId);
  };

  const themeFields = [
    { key: 'theme_primary_hex', label: 'Color primario (botones, barra superior)' },
    { key: 'theme_background_hex', label: 'Fondo del sitio' },
    { key: 'theme_foreground_hex', label: 'Texto principal' },
    { key: 'theme_accent_hex', label: 'Acento (detalles)' },
    { key: 'theme_muted_hex', label: 'Bordes y fondos suaves' },
  ];

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft) return;
    setSaving(true);
    try {
      const services_bullets = servicesText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const payload = {
        ...draft,
        services_bullets,
        home_features: featureRows.map((r) => ({
          icon: r.icon || 'shield',
          title: r.title || '',
          desc: r.desc || '',
        })),
      };
      await updateClinicInfo(payload);
      setDraft(payload);
      await refreshBranding();
      toast.success('Cambios del sitio público guardados.');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading || !draft) {
    return (
      <div className="text-muted-foreground animate-pulse" data-testid="admin-site-loading">
        Cargando editor del sitio…
      </div>
    );
  }

  return (
    <div className="max-w-7xl space-y-8" data-testid="admin-site-public">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Sitio Público</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Solo el super administrador puede editar esto. Cada bloque muestra a la derecha cómo queda
          en la página; los cambios se ven en vivo y se publican al guardar.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        <SectionCard
          title="Marca"
          hint={PIZAS_MODE ? 'Nombre de la pizzería, logo, título de pestaña y textos del sitio.' : 'Nombre, logo y textos generales del sitio.'}
          preview={
            <BrandingPreview
              name={draft.name}
              logoUrl={draft.logo_url}
              phone={draft.phone}
              email={draft.email}
              address={draft.address}
              footerBlurb={draft.footer_blurb}
              copyrightName={draft.copyright_name}
            />
          }
        >
          <SiteImageField
            label="Logotipo"
            url={draft.logo_url}
            onChange={(u) => setField('logo_url', u)}
            inputId="logo_url"
            previewVariant="logo"
          />
          <div className="space-y-2">
            <Label htmlFor="name">{PIZAS_MODE ? 'Nombre de la pizzería (título del sitio)' : 'Nombre de la empresa / clínica'}</Label>
            <Input id="name" value={draft.name} onChange={(e) => setField('name', e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="site_tagline_short">Línea corta (barra superior)</Label>
            <Input
              id="site_tagline_short"
              value={draft.site_tagline_short || ''}
              onChange={(e) => setField('site_tagline_short', e.target.value || null)}
              placeholder="Opcional; si está vacío se usa la dirección resumida"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Descripción breve (meta / referencia)</Label>
            <Textarea
              id="description"
              rows={3}
              value={draft.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="footer_blurb">Texto del pie de página (columna izquierda)</Label>
            <Textarea
              id="footer_blurb"
              rows={3}
              value={draft.footer_blurb || ''}
              onChange={(e) => setField('footer_blurb', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="copyright_name">Nombre en copyright</Label>
            <Input
              id="copyright_name"
              value={draft.copyright_name || ''}
              onChange={(e) => setField('copyright_name', e.target.value || null)}
              placeholder="Por defecto: nombre de la empresa"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Tema y colores"
          hint="Elige una combinación predefinida o ajusta cada color. La vista previa de la derecha refleja los colores al instante."
          preview={
            <ThemePreview
              name={draft.name}
              ctaButtonLabel={draft.cta_button_label}
              primaryHex={draft.theme_primary_hex}
              backgroundHex={draft.theme_background_hex}
              foregroundHex={draft.theme_foreground_hex}
              showCheckerboard={draft.theme_show_checkerboard}
            />
          }
        >
          <div className="space-y-2 max-w-md">
            <Label htmlFor="theme-preset">Combinación de colores</Label>
            <Select value={themePresetId} onValueChange={applyThemePreset}>
              <SelectTrigger id="theme-preset" className="h-11">
                <SelectValue placeholder="Elegir paleta…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Personalizado (colores actuales)</SelectItem>
                {THEME_PRESETS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-flex h-4 w-4 shrink-0 rounded-full border border-border"
                        style={{
                          background: `linear-gradient(135deg, ${p.theme_primary_hex} 50%, ${p.theme_accent_hex} 50%)`,
                        }}
                        aria-hidden
                      />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              12 paletas: rojos, naranjas, azules, verdes y amarillos. Puedes retocar los selectores después.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {themeFields.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <input
                  type="color"
                  id={key}
                  className="h-11 w-14 cursor-pointer rounded border border-border p-1"
                  value={(draft[key] || '#C62828').replace(/^([^#])/, '#$1')}
                  onChange={(e) => setField(key, e.target.value)}
                />
                <div className="flex-1 space-y-1">
                  <Label htmlFor={key}>{label}</Label>
                  <Input
                    value={draft[key] || ''}
                    onChange={(e) => setField(key, e.target.value)}
                    placeholder="#C62828"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <p className="font-medium text-sm">Patrón tablero (retro)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Franjas a cuadros en el hero y reseñas, como pizzería clásica.
              </p>
            </div>
            <Switch
              checked={Boolean(draft.theme_show_checkerboard)}
              onCheckedChange={(v) => setField('theme_show_checkerboard', v)}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Navegación y botones"
          hint="Etiquetas del menú superior y rutas de los botones principales."
          preview={
            <NavPreview
              name={draft.name}
              logoUrl={draft.logo_url}
              phone={draft.phone}
              email={draft.email}
              siteTaglineShort={draft.site_tagline_short}
              address={draft.address}
              navHome={draft.nav_label_home}
              navMenu={draft.nav_label_menu}
              navReviews={draft.nav_label_reviews}
              navContact={draft.nav_label_contact}
              ctaButtonLabel={draft.cta_button_label}
            />
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nav_label_home">Menú — Inicio</Label>
              <Input id="nav_label_home" value={draft.nav_label_home || ''} onChange={(e) => setField('nav_label_home', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nav_label_menu">{PIZAS_MODE ? 'Menú — pizzas' : 'Menú — servicios'}</Label>
              <Input id="nav_label_menu" value={draft.nav_label_menu || ''} onChange={(e) => setField('nav_label_menu', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nav_path_menu">Ruta del ítem menú</Label>
              <Input
                id="nav_path_menu"
                value={draft.nav_path_menu || ''}
                onChange={(e) => setField('nav_path_menu', e.target.value)}
                placeholder="/pedir"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nav_label_reviews">Menú — reseñas</Label>
              <Input id="nav_label_reviews" value={draft.nav_label_reviews || ''} onChange={(e) => setField('nav_label_reviews', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nav_label_contact">Menú — contacto</Label>
              <Input id="nav_label_contact" value={draft.nav_label_contact || ''} onChange={(e) => setField('nav_label_contact', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2 border-t border-border pt-4">
              <p className="text-sm font-medium">Botón del header (esquina derecha)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_button_label">Texto</Label>
              <Input id="cta_button_label" value={draft.cta_button_label || ''} onChange={(e) => setField('cta_button_label', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_button_path">Ruta</Label>
              <Input id="cta_button_path" value={draft.cta_button_path || ''} onChange={(e) => setField('cta_button_path', e.target.value)} placeholder="/pedir" />
            </div>
            <div className="space-y-2 sm:col-span-2 border-t border-border pt-4">
              <p className="text-sm font-medium">Botones del hero (inicio)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_cta_primary_label">Principal — texto</Label>
              <Input id="hero_cta_primary_label" value={draft.hero_cta_primary_label || ''} onChange={(e) => setField('hero_cta_primary_label', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_cta_primary_path">Principal — ruta</Label>
              <Input id="hero_cta_primary_path" value={draft.hero_cta_primary_path || ''} onChange={(e) => setField('hero_cta_primary_path', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_cta_secondary_label">Secundario — texto</Label>
              <Input id="hero_cta_secondary_label" value={draft.hero_cta_secondary_label || ''} onChange={(e) => setField('hero_cta_secondary_label', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_cta_secondary_path">Secundario — ruta</Label>
              <Input id="hero_cta_secondary_path" value={draft.hero_cta_secondary_path || ''} onChange={(e) => setField('hero_cta_secondary_path', e.target.value)} />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Contacto del sitio web"
          hint="Teléfono, WhatsApp, correo y dirección se editan en «Identidad y contacto» (más abajo) y se muestran en la cabecera, el pie de página, /contacto y los textos legales. La columna «Horarios» se genera automáticamente desde el menú Horarios."
          preview={
            <BrandingPreview
              name={draft.name}
              logoUrl={draft.logo_url}
              phone={draft.phone}
              email={draft.email}
              address={draft.address}
              footerBlurb={draft.footer_blurb}
              copyrightName={draft.copyright_name}
            />
          }
        >
          {/* Paquete legal v2026-10-09: el contacto ya no se edita aquí (el backend lo ignora en este guardado) */}
          <div className="text-sm space-y-1 rounded-md border p-3 bg-muted/30" data-testid="site-contact-readonly">
            <p>Teléfono: <strong>{draft.phone || 'dato pendiente'}</strong></p>
            <p>WhatsApp: <strong>{draft.whatsapp || draft.phone || 'dato pendiente'}</strong></p>
            <p>Correo: <strong>{draft.email || 'dato pendiente'}</strong></p>
            <p>Dirección: <strong>{[draft.address, draft.city].filter(Boolean).join(', ') || 'dato pendiente'}</strong></p>
            <a href="#identidad-y-contacto" className="underline text-primary">Editar en «Identidad y contacto»</a>
          </div>
        </SectionCard>

        <SectionCard
          title="Hero (inicio)"
          hint="Primera pantalla del sitio: distintivo, título, subtítulo, imagen y botones."
          preview={
            <HeroPreview
              brandName={draft.name}
              badge={draft.hero_badge_text}
              titlePrefix={draft.hero_title_prefix}
              titleHighlight={draft.hero_title_highlight}
              subtitle={draft.hero_subtitle}
              heroImageUrl={draft.hero_image_url}
              ctaPrimaryLabel={draft.hero_cta_primary_label}
              ctaSecondaryLabel={draft.hero_cta_secondary_label}
              showCheckerboard={draft.theme_show_checkerboard}
            />
          }
        >
          <div className="space-y-2">
            <Label htmlFor="hero_badge_text">Texto del distintivo (badge)</Label>
            <Input
              id="hero_badge_text"
              value={draft.hero_badge_text || ''}
              onChange={(e) => setField('hero_badge_text', e.target.value || null)}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hero_title_prefix">Título — parte antes del acento</Label>
              <Input
                id="hero_title_prefix"
                value={draft.hero_title_prefix || ''}
                onChange={(e) => setField('hero_title_prefix', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_title_highlight">Título — palabras en color primario</Label>
              <Input
                id="hero_title_highlight"
                value={draft.hero_title_highlight || ''}
                onChange={(e) => setField('hero_title_highlight', e.target.value || null)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hero_subtitle">Subtítulo</Label>
            <Textarea
              id="hero_subtitle"
              rows={3}
              value={draft.hero_subtitle || ''}
              onChange={(e) => setField('hero_subtitle', e.target.value || null)}
            />
          </div>
          <SiteImageField
            label="Imagen principal (hero)"
            url={draft.hero_image_url}
            onChange={(u) => setField('hero_image_url', u)}
            inputId="hero_image_url"
            previewVariant="hero"
          />
        </SectionCard>

        <SectionCard
          title="Tarjetas de valores (4 columnas)"
          hint="Cuatro columnas con icono, título y texto breve, debajo del hero."
          preview={<FeaturesPreview featureRows={featureRows} />}
        >
          {featureRows.map((row, i) => (
            <div key={i} className="grid gap-3 p-4 border border-border rounded-xl sm:grid-cols-12 items-end">
              <div className="sm:col-span-3 space-y-2">
                <Label>Icono (Lucide)</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={row.icon}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, icon: v } : r)));
                  }}
                >
                  {FEATURE_ICON_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-4 space-y-2">
                <Label>Título</Label>
                <Input
                  value={row.title}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, title: v } : r)));
                  }}
                />
              </div>
              <div className="sm:col-span-4 space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={row.desc}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, desc: v } : r)));
                  }}
                />
              </div>
              <div className="sm:col-span-1 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => setFeatureRows((rows) => rows.filter((_, j) => j !== i))}
                  disabled={featureRows.length <= 1}
                  aria-label="Eliminar fila"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => setFeatureRows((rows) => [...rows, { icon: 'shield', title: '', desc: '' }])}
          >
            <Plus className="w-4 h-4" />
            Añadir tarjeta
          </Button>
        </SectionCard>

        <SectionCard
          title="Servicios (lista con viñetas)"
          hint="Sección «¿Por qué elegirnos?»: título, párrafo, lista e imagen lateral."
          preview={
            <ServicesSectionPreview
              title={draft.services_section_title}
              intro={draft.services_section_intro}
              bulletsText={servicesText}
              imageUrl={draft.clinic_section_image_url}
              brandName={draft.name}
            />
          }
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="services_section_title">Título de sección</Label>
              <Input
                id="services_section_title"
                value={draft.services_section_title || ''}
                onChange={(e) => setField('services_section_title', e.target.value || null)}
              />
            </div>
            <div className="sm:col-span-2">
              <SiteImageField
                label="Imagen lateral (sección servicios)"
                url={draft.clinic_section_image_url}
                onChange={(u) => setField('clinic_section_image_url', u)}
                inputId="clinic_section_image_url"
                previewVariant="banner"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="services_section_intro">Párrafo introductorio</Label>
            <Textarea
              id="services_section_intro"
              rows={3}
              value={draft.services_section_intro || ''}
              onChange={(e) => setField('services_section_intro', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="services_bullets">Ítems (uno por línea)</Label>
            <Textarea id="services_bullets" rows={8} value={servicesText} onChange={(e) => setServicesText(e.target.value)} />
          </div>
        </SectionCard>

        <SectionCard
          title="Secciones inferiores"
          hint="Productos destacados, reseñas y la franja de llamada a la acción (CTA)."
          preview={
            <LowerSectionsPreview
              featuredProductsTitle={draft.featured_products_title}
              featuredProductsIntro={draft.featured_products_intro}
              reviewsSectionTitle={draft.reviews_section_title}
              ctaTitle={draft.cta_title}
              ctaSubtitle={draft.cta_subtitle}
              ctaButtonLabel={draft.cta_button_label}
            />
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="featured_products_title">Título — productos destacados</Label>
              <Input
                id="featured_products_title"
                value={draft.featured_products_title || ''}
                onChange={(e) => setField('featured_products_title', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reviews_section_title">Título — reseñas</Label>
              <Input
                id="reviews_section_title"
                value={draft.reviews_section_title || ''}
                onChange={(e) => setField('reviews_section_title', e.target.value || null)}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="featured_products_intro">Intro — productos destacados</Label>
              <Textarea
                id="featured_products_intro"
                rows={2}
                value={draft.featured_products_intro || ''}
                onChange={(e) => setField('featured_products_intro', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_title">CTA — título</Label>
              <Input id="cta_title" value={draft.cta_title || ''} onChange={(e) => setField('cta_title', e.target.value || null)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_subtitle">CTA — subtítulo</Label>
              <Input
                id="cta_subtitle"
                value={draft.cta_subtitle || ''}
                onChange={(e) => setField('cta_subtitle', e.target.value || null)}
              />
            </div>
          </div>
        </SectionCard>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="gap-2 rounded-full">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>

      {/* Paquete legal v2026-10-09: formularios propios (no anidados en el de arriba) */}
      <LegalIdentityCard
        onSaved={async () => {
          await refreshBranding();
          try {
            const d = await getClinicInfo();
            setDraft((prev) => (prev ? { ...prev, phone: d.phone, whatsapp: d.whatsapp, email: d.email, address: d.address, city: d.city } : prev));
          } catch {
            /* nada */
          }
        }}
      />
      <LegalCommercialCard />
    </div>
  );
}
